import { confirmSignIn, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import type { AuthUser } from 'aws-amplify/auth';

import { captureException } from '@jerry-serverless/next-sentry';

import { getPhoneNumberWithContryCode, getAuthCookie, AUTH_COOKIE_NAME } from './utils';
import { isError } from './types';

/**
 * Sign out from the Cognito userpool.
 * It will clear the status information in client storage which prevents you from calling the `signIn` method again.
 */
async function signOutCognito(): Promise<void> {
  let authUser: AuthUser | null = null;
  try {
    authUser = await getCurrentUser();
  } catch (err) {
    // noop
  }
  if (authUser && authUser.userId && authUser.username) {
    // @NOTE: we must wait for the sign out to finish to keep user having a right state, it's a async operation, it might take a while
    await signOut();
  }
}

/**
 * Sign in the Cognito user pool
 */
async function signInCognito({ phone }: { phone: string }, recursiveCallCount = 0): Promise<void> {
  try {
    // we use user's phone number as the username and we need to add the country code to the phone number
    await signIn({
      username: getPhoneNumberWithContryCode(phone),
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    });
  } catch (err) {
    if (isError(err) && recursiveCallCount < 1) {
      if (err.name === 'UserAlreadyAuthenticatedException') {
        // we only allow one time recursive call
        // if the user already singed in the Cognito, we need to sign out first
        await signOutCognito();
        // then we can start the auth flow again, in order to prevent infinite loop, we need to count the recursive call and limit it
        await signInCognito({ phone }, recursiveCallCount + 1);

        return;
      }
    }
    throw err;
  }
}

/**
 * Submit credentials to confirm the sign in request in Cognito user pool
 */
async function confirmSignInCognitoByAuthCookie(): Promise<void> {
  const authCookie = getAuthCookie();
  if (authCookie) {
    const result = await confirmSignIn({ challengeResponse: `${AUTH_COOKIE_NAME}=${authCookie}` });
    const { nextStep } = result;
    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
      // the challenge answer is not correct
      throw new Error('the provided auth cookie is not applicable.');
    }
  } else {
    throw new Error('No auth cookie found!');
  }
}

/**
 * Get the Cognito auth credentials by the auth cookie,
 * the obtained credentials data will be saved in localStorage automatically
 * @param phone user phone number
 * @param authCookie the final `jerryauth` cookie item issued when user successfully logs in the app.
 */
export const getCognitoAuthCredentials = async ({ phone }: { phone: string }): Promise<void> => {
  try {
    await signInCognito({ phone });
    await confirmSignInCognitoByAuthCookie();
  } catch (err) {
    // report error, don't throw the error so that it won't block the existing signup flow
    captureException(err);
  }
};
